import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';

/**
 * Hook to fetch NIP-23 long-form content articles for a specific pubkey
 */
export function useArticles(pubkey: string | undefined, enabled = true) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['articles', pubkey],
    queryFn: async (c) => {
      if (!pubkey) return [];

      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(5000)]);

      const events = await nostr.query([
        {
          kinds: [30023], // NIP-23 long-form content
          authors: [pubkey],
          limit: 50,
        }
      ], { signal });

      // Filter valid article events
      return events
        .filter(validateArticleEvent)
        .sort((a, b) => b.created_at - a.created_at);
    },
    enabled: !!pubkey && enabled,
    staleTime: 0, // Always consider data stale to refetch on tab switch
  });
}

/**
 * Hook to fetch a single article by d-tag
 */
export function useArticle(pubkey: string | undefined, dTag: string | undefined) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['article', pubkey, dTag],
    queryFn: async (c) => {
      if (!pubkey || !dTag) return null;

      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(3000)]);

      const events = await nostr.query([
        {
          kinds: [30023],
          authors: [pubkey],
          '#d': [dTag],
          limit: 1,
        }
      ], { signal });

      const event = events[0];
      return event && validateArticleEvent(event) ? event : null;
    },
    enabled: !!pubkey && !!dTag,
  });
}

/**
 * Validator for NIP-23 article events
 */
export function validateArticleEvent(event: NostrEvent): boolean {
  if (event.kind !== 30023) return false;

  // Must have a d tag
  const hasD = event.tags.some(([name]) => name === 'd');
  if (!hasD) return false;

  return true;
}

/**
 * Extract article title from event
 */
export function extractArticleTitle(event: NostrEvent): string | null {
  const titleTag = event.tags.find(([name]) => name === 'title');
  return titleTag?.[1] || null;
}

/**
 * Extract article summary from event
 */
export function extractArticleSummary(event: NostrEvent): string | null {
  const summaryTag = event.tags.find(([name]) => name === 'summary');
  return summaryTag?.[1] || null;
}

/**
 * Extract article image from event
 */
export function extractArticleImage(event: NostrEvent): string | null {
  const imageTag = event.tags.find(([name]) => name === 'image');
  return imageTag?.[1] || null;
}

/**
 * Extract article d-tag identifier
 */
export function extractArticleDTag(event: NostrEvent): string | null {
  const dTag = event.tags.find(([name]) => name === 'd');
  return dTag?.[1] || null;
}

/**
 * Extract article published_at timestamp
 */
export function extractArticlePublishedAt(event: NostrEvent): number | null {
  const publishedTag = event.tags.find(([name]) => name === 'published_at');
  if (!publishedTag?.[1]) return null;

  const timestamp = parseInt(publishedTag[1]);
  return isNaN(timestamp) ? null : timestamp;
}

/**
 * Extract article tags
 */
export function extractArticleTags(event: NostrEvent): string[] {
  return event.tags
    .filter(([name]) => name === 't')
    .map(([, value]) => value)
    .filter((tag): tag is string => !!tag);
}
